import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const buildUsersBaseUrl = (apiBase) => {
  const normalized = String(apiBase || '').replace(/\/+$/, '');
  if (normalized.endsWith('/api/v1')) {
    return `${normalized}/users`;
  }
  if (normalized.endsWith('/api')) {
    return `${normalized}/v1/users`;
  }
  return `${normalized}/api/v1/users`;
};

const getErrorMessage = (payload, fallback) => {
  return payload?.error?.message || payload?.message || fallback;
};

const rolePermissions = {
  admin: ['agent:read', 'agent:invoke', 'agent:manage', 'user:read', 'user:manage'],
  operator: ['agent:read', 'agent:invoke', 'user:read'],
  reader: ['agent:read'],
};

const availableModules = [
  'SCADA_MIGRATION',
  'SCADA',
  'CAPITAL_PROJECTS',
  'LOGIC_CONVERSION',
  'SQL_OPTIMIZATION',
  'TAG_VALIDATION',
  'VIDEOS_LIBRARY',
];

const defaultModuleAccessEntry = () => ({
  module: 'SCADA_MIGRATION',
  role: 'reader',
});

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );

    return JSON.parse(json);
  } catch (_error) {
    return null;
  }
};

const derivePermissionsFromAuth = (auth) => {
  const explicitPermissions = Array.isArray(auth?.user?.permissions)
    ? auth.user.permissions
    : Array.isArray(auth?.permissions)
      ? auth.permissions
      : [];

  if (explicitPermissions.length > 0) {
    return explicitPermissions;
  }

  const tokenPayload = decodeJwtPayload(auth?.accessToken);
  const tokenPermissions = Array.isArray(tokenPayload?.permissions)
    ? tokenPayload.permissions
    : typeof tokenPayload?.scope === 'string'
      ? tokenPayload.scope.split(' ').filter(Boolean)
      : [];

  if (tokenPermissions.length > 0) {
    return tokenPermissions;
  }

  const roles = Array.isArray(tokenPayload?.roles)
    ? tokenPayload.roles
    : tokenPayload?.role
      ? [tokenPayload.role]
      : [];

  return [...new Set(roles.flatMap((role) => rolePermissions[String(role)] || []))];
};

function Configuration({ auth, onAuthExpired, midlayerServiceUrl }) {
  const navigate = useNavigate();
  const defaultNewUser = {
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    moduleAccess: [defaultModuleAccessEntry()],
    isActive: true,
  };
  const [activeTab, setActiveTab] = useState('general');
  const [config, setConfig] = useState({
    apiUrl: '',
    apikey: '',
    company_logo: '',
  });
  const [saveStatus, setSaveStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [userActionStatus, setUserActionStatus] = useState('');
  const [newUser, setNewUser] = useState(defaultNewUser);
  const [editingUserEmail, setEditingUserEmail] = useState('');

  const permissions = derivePermissionsFromAuth(auth);
  const isAdminUser = permissions.includes('user:manage');

  const getAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (auth?.accessToken) {
      headers.Authorization = `Bearer ${auth.accessToken}`;
    }
    return headers;
  };

  const handleAuthError = (responseStatus) => {
    if (responseStatus === 401 && onAuthExpired) {
      onAuthExpired();
    }
  };

  const fetchUsers = async () => {
    if (!isAdminUser) {
      return;
    }

    setUsersLoading(true);
    setUsersError('');

    try {
      const usersUrl = `${buildUsersBaseUrl(midlayerServiceUrl)}?limit=100&skip=0`;
      const response = await fetch(usersUrl, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAuthError(response.status);
        throw new Error(getErrorMessage(payload, 'Failed to load users.'));
      }

      setUsers(Array.isArray(payload?.data) ? payload.data : []);
    } catch (requestError) {
      setUsersError(requestError.message || 'Unable to fetch users.');
    } finally {
      setUsersLoading(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setUserActionStatus('');
    setUsersError('');

    try {
      const now = new Date().toISOString();
      const moduleAccessPayload = newUser.moduleAccess
        .map((entry) => ({
          module: String(entry?.module || '').trim(),
          role: String(entry?.role || 'reader').trim(),
        }))
        .filter((entry) => entry.module);

      if (moduleAccessPayload.length === 0) {
        throw new Error('Please add at least one module access entry.');
      }

      const response = await fetch(buildUsersBaseUrl(midlayerServiceUrl), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          username: newUser.username.trim(),
          email: newUser.email.trim(),
          password: newUser.password,
          firstName: newUser.firstName.trim(),
          lastName: newUser.lastName.trim(),
          isActive: Boolean(newUser.isActive),
          moduleAccess: moduleAccessPayload.map((entry) => ({
            module: entry.module,
            role: entry.role,
            accessValidFrom: now,
            accessValidTo: null,
            isActive: true,
          })),
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAuthError(response.status);
        throw new Error(getErrorMessage(payload, 'Failed to create user.'));
      }

      setUserActionStatus('User created successfully.');
      setNewUser(defaultNewUser);
      fetchUsers();
    } catch (requestError) {
      setUsersError(requestError.message || 'Unable to create user.');
    }
  };

  const updateUser = async (e) => {
    e.preventDefault();
    if (!editingUserEmail) {
      return;
    }

    setUserActionStatus('');
    setUsersError('');

    try {
      const now = new Date().toISOString();
      const moduleAccessPayload = newUser.moduleAccess
        .map((entry) => ({
          module: String(entry?.module || '').trim(),
          role: String(entry?.role || 'reader').trim(),
        }))
        .filter((entry) => entry.module);

      if (moduleAccessPayload.length === 0) {
        throw new Error('Please add at least one module access entry.');
      }

      const requestBody = {
        username: newUser.username.trim(),
        email: editingUserEmail,
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        isActive: Boolean(newUser.isActive),
        moduleAccess: moduleAccessPayload.map((entry) => ({
          module: entry.module,
          role: entry.role,
          accessValidFrom: now,
          accessValidTo: null,
          isActive: true,
        })),
      };

      if (newUser.password.trim()) {
        requestBody.password = newUser.password;
      }

      const response = await fetch(`${buildUsersBaseUrl(midlayerServiceUrl)}/${encodeURIComponent(editingUserEmail)}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAuthError(response.status);
        throw new Error(getErrorMessage(payload, 'Failed to update user.'));
      }

      setUserActionStatus('User updated successfully.');
      setEditingUserEmail('');
      setNewUser(defaultNewUser);
      fetchUsers();
    } catch (requestError) {
      setUsersError(requestError.message || 'Unable to update user.');
    }
  };

  const startEditUser = (userItem) => {
    const normalizedModuleAccess = Array.isArray(userItem?.moduleAccess) && userItem.moduleAccess.length > 0
      ? userItem.moduleAccess.map((entry) => ({
          module: String(entry?.module || '').trim() || 'SCADA_MIGRATION',
          role: String(entry?.role || 'reader').trim() || 'reader',
        }))
      : [defaultModuleAccessEntry()];

    setEditingUserEmail(userItem.email || '');
    setNewUser({
      username: userItem.username || '',
      email: userItem.email || '',
      password: '',
      firstName: userItem.firstName || '',
      lastName: userItem.lastName || '',
      moduleAccess: normalizedModuleAccess,
      isActive: userItem.isActive !== false,
    });
    setUserActionStatus('');
    setUsersError('');
  };

  const cancelEditUser = () => {
    setEditingUserEmail('');
    setNewUser(defaultNewUser);
  };

  const addModuleAccessRow = () => {
    setNewUser((prev) => ({
      ...prev,
      moduleAccess: [...prev.moduleAccess, defaultModuleAccessEntry()],
    }));
  };

  const updateModuleAccessRow = (index, field, value) => {
    setNewUser((prev) => ({
      ...prev,
      moduleAccess: prev.moduleAccess.map((entry, entryIndex) => (
        entryIndex === index ? { ...entry, [field]: value } : entry
      )),
    }));
  };

  const removeModuleAccessRow = (index) => {
    setNewUser((prev) => {
      if (prev.moduleAccess.length === 1) {
        return prev;
      }

      return {
        ...prev,
        moduleAccess: prev.moduleAccess.filter((_, entryIndex) => entryIndex !== index),
      };
    });
  };

  const toggleUserLock = async (email, isLocked) => {
    setUserActionStatus('');
    setUsersError('');

    try {
      const response = await fetch(`${buildUsersBaseUrl(midlayerServiceUrl)}/${encodeURIComponent(email)}/lock`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isLocked: !isLocked }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAuthError(response.status);
        throw new Error(getErrorMessage(payload, 'Failed to update lock status.'));
      }

      setUserActionStatus(`User ${!isLocked ? 'locked' : 'unlocked'} successfully.`);
      fetchUsers();
    } catch (requestError) {
      setUsersError(requestError.message || 'Unable to update lock status.');
    }
  };

  const toggleUserActive = async (userItem) => {
    setUserActionStatus('');
    setUsersError('');

    try {
      const response = await fetch(`${buildUsersBaseUrl(midlayerServiceUrl)}/${encodeURIComponent(userItem.email)}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !userItem.isActive }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAuthError(response.status);
        throw new Error(getErrorMessage(payload, 'Failed to update user status.'));
      }

      setUserActionStatus(`User ${!userItem.isActive ? 'activated' : 'deactivated'} successfully.`);
      fetchUsers();
    } catch (requestError) {
      setUsersError(requestError.message || 'Unable to update user status.');
    }
  };

  const deleteUser = async (email) => {
    setUserActionStatus('');
    setUsersError('');

    const shouldDelete = window.confirm(`Delete user ${email}? This action cannot be undone.`);
    if (!shouldDelete) {
      return;
    }

    try {
      const response = await fetch(`${buildUsersBaseUrl(midlayerServiceUrl)}/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        handleAuthError(response.status);
        throw new Error(getErrorMessage(payload, 'Failed to delete user.'));
      }

      setUserActionStatus('User deleted successfully.');
      fetchUsers();
    } catch (requestError) {
      setUsersError(requestError.message || 'Unable to delete user.');
    }
  };

  useEffect(() => {
    const savedConfig = localStorage.getItem('scada_api_config');
    const defaultBase = process.env.REACT_APP_API_BASE || 'http://localhost:20021/api';
    const defaultApiKey = process.env.REACT_APP_API_KEY || '';

    if (savedConfig) {
      const parsed = JSON.parse(savedConfig);
      setConfig({
        apiUrl: parsed.apiUrl || parsed.apiBase || parsed.tag_conversion || defaultBase,
        apikey: parsed.apikey !== undefined ? parsed.apikey : defaultApiKey,
        company_logo: parsed.company_logo || '',
      });
    } else {
      setConfig({
        apiUrl: defaultBase,
        apikey: defaultApiKey,
        company_logo: '',
      });
    }
  }, []);

  useEffect(() => {
    if (config.apiUrl && auth?.accessToken && isAdminUser) {
      fetchUsers();
    }
  }, [config.apiUrl, auth?.accessToken, isAdminUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfig(prev => ({ ...prev, company_logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setConfig(prev => ({ ...prev, company_logo: '' }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    localStorage.setItem('scada_api_config', JSON.stringify(config));
    window.dispatchEvent(new Event('configUpdated'));
    setSaveStatus('Configuration saved successfully!');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const handleReset = () => {
    const defaultBase = process.env.REACT_APP_API_BASE || 'http://localhost:20021/api';
    const resetConfig = {
      apiUrl: defaultBase,
      apikey: process.env.REACT_APP_API_KEY || '',
      company_logo: '',
    };
    setConfig(resetConfig);
    localStorage.setItem('scada_api_config', JSON.stringify(resetConfig));
    window.dispatchEvent(new Event('configUpdated'));
    setSaveStatus('Reset to environment defaults');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  return (
    <div>
      <div className="back-button" onClick={() => navigate('/')}>
        ← Back to Home
      </div>

      <div className="page-header">
        <h2 className="page-title">Configuration</h2>
        <p className="page-subtitle">
          Configure API endpoint and settings
        </p>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General Settings
          </button>
          {isAdminUser && (
            <button
              className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              User Settings
            </button>
          )}
        </div>

        {activeTab === 'general' && (
          <div className="tab-content">
            <div className="card">
              <h3 className="card-title">General Settings</h3>
        <div className="form-group">
          <label>Company Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginTop: '0.5rem' }}>
            {config.company_logo ? (
              <div style={{ position: 'relative' }}>
                <img 
                  src={config.company_logo} 
                  alt="Company Logo Preview" 
                  style={{ height: '60px', borderRadius: '4px', border: '1px solid var(--border)', padding: '4px' }}
                />
                <button 
                  onClick={removeLogo}
                  style={{ 
                    position: 'absolute', 
                    top: '-8px', 
                    right: '-8px', 
                    background: '#ef4444', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '50%', 
                    width: '20px', 
                    height: '20px', 
                    fontSize: '12px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div 
                style={{ 
                  height: '60px', 
                  width: '120px', 
                  border: '2px dashed var(--border)', 
                  borderRadius: '4px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#64748b',
                  fontSize: '0.8rem'
                }}
              >
                No Logo
              </div>
            )}
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                id="logo-upload"
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="btn btn-secondary btn-sm"
                onClick={() => document.getElementById('logo-upload').click()}
              >
                Upload New Logo
              </button>
              <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                Recommended: Transparent PNG, height 40px
              </p>
            </div>
          </div>
        </div>

        <h3 className="card-title" style={{ marginTop: '2rem' }}>API Settings</h3>
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>API Base URL</label>
            <input
              type="text"
              name="apiUrl"
              value={config.apiUrl}
              onChange={handleChange}
              placeholder="e.g., http://localhost:20021/api"
            />
            <small style={{ color: '#64748b' }}>Used for all agent pages.</small>
          </div>

          <div className="form-group">
            <label>API Key (apikey header)</label>
            <input
              type="password"
              name="apikey"
              value={config.apikey}
              onChange={handleChange}
              placeholder="Enter your API Key"
            />
            <small style={{ color: '#64748b' }}>Used for authenticated requests.</small>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="submit" className="btn btn-primary">
              Save Configuration
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Reset to Defaults
            </button>
          </div>
        </form>
            </div>
          </div>
        )}

        {isAdminUser && activeTab === 'users' && (
          <div className="tab-content">
            <div className="card" style={{ cursor: 'default' }}>
              <h3 className="card-title">User Management</h3>

          {editingUserEmail && (
            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              Currently editing: <strong>{editingUserEmail}</strong>
            </div>
          )}

          <form onSubmit={editingUserEmail ? updateUser : createUser}>
            <div className="user-form-grid">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  disabled={Boolean(editingUserEmail)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{editingUserEmail ? 'Password (optional)' : 'Password'}</label>
                <input
                  type="password"
                  minLength={8}
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  required={!editingUserEmail}
                />
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Module Access</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {newUser.moduleAccess.map((entry, index) => (
                    <div key={`${entry.module}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.6rem', alignItems: 'center' }}>
                      <select
                        value={entry.module}
                        onChange={(e) => updateModuleAccessRow(index, 'module', e.target.value)}
                        required
                      >
                        {availableModules.map((moduleName) => (
                          <option key={moduleName} value={moduleName}>{moduleName}</option>
                        ))}
                      </select>
                      <select
                        value={entry.role}
                        onChange={(e) => updateModuleAccessRow(index, 'role', e.target.value)}
                        required
                      >
                        <option value="admin">admin</option>
                        <option value="operator">operator</option>
                        <option value="reader">reader</option>
                      </select>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => removeModuleAccessRow(index)}
                        disabled={newUser.moduleAccess.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={addModuleAccessRow}>
                      Add Module
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label style={{ marginBottom: '0.6rem' }}>User Status</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={newUser.isActive}
                    onChange={(e) => setNewUser(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Active
                </label>
              </div>
            </div>
            <button type="submit" className="btn btn-primary">
              {editingUserEmail ? 'Update User' : 'Create User'}
            </button>
            {editingUserEmail && (
              <button type="button" className="btn btn-secondary" style={{ marginLeft: '0.75rem' }} onClick={cancelEditUser}>
                Cancel Edit
              </button>
            )}
            <button type="button" className="btn btn-secondary" style={{ marginLeft: '0.75rem' }} onClick={fetchUsers}>
              Refresh Users
            </button>
          </form>

          {usersError && <div className="alert alert-error" style={{ marginTop: '1rem' }}>{usersError}</div>}
          {userActionStatus && <div className="alert alert-success" style={{ marginTop: '1rem' }}>{userActionStatus}</div>}

          <div style={{ marginTop: '1.25rem' }}>
            <h4 style={{ marginBottom: '0.75rem' }}>Existing Users</h4>
            {usersLoading ? (
              <p style={{ color: 'var(--text-secondary)' }}>Loading users...</p>
            ) : users.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>No users found.</p>
            ) : (
              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Active</th>
                      <th>Locked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userItem) => (
                      <tr key={userItem.email}>
                        <td>{`${userItem.firstName || ''} ${userItem.lastName || ''}`.trim() || userItem.username || '-'}</td>
                        <td>{userItem.email}</td>
                        <td>{(userItem.moduleAccess || []).map(m => `${m.module}:${m.role}`).join(', ') || '-'}</td>
                        <td>{userItem.isActive ? 'Yes' : 'No'}</td>
                        <td>{userItem.isLocked ? 'Yes' : 'No'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => startEditUser(userItem)}>
                              {editingUserEmail === userItem.email ? 'Editing' : 'Edit'}
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleUserActive(userItem)}>
                              {userItem.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleUserLock(userItem.email, Boolean(userItem.isLocked))}>
                              {userItem.isLocked ? 'Unlock' : 'Lock'}
                            </button>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteUser(userItem.email)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
            </div>
          </div>
        )}
      </div>

      {saveStatus && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <div className="alert-content">
            <div>{saveStatus}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Configuration;
